import { useEffect, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import ToggleButton from 'components/widgets/ProjectInsights/SearchResultsViews/ToggleButton';
import { useMapProjectList } from 'hooks/projectMap/useMapProjectList';
import { usePaginate } from 'hooks/usePaginate';
import ProjectList from 'pages/ProjectInsights/ProjectLists/SearchResults/List';
import { useSelectedPolygonBoundsOnMap } from 'state/map';
import { useAddressQuery } from 'state/queryParams';
import { formatInteger } from 'utils/format';

const containerStyle = {
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: 'padding_8',
};

const projectsFoundStyle = {
  fontSize: '16px',
  fontWeight: '600',
};

const resultText = {
  fontSize: '13px',
  gap: 'padding_4',
};

const paginationContainer = {
  mt: 'padding_8',
  mb: 'padding_16',
};

const PAGE_SIZE = 20;

const TOP_LIST_ID = 'top-list';

const RenderResults = () => {
  const { projectList } = useMapProjectList();
  const [selectedPolygonBoundsOnMap] = useSelectedPolygonBoundsOnMap();

  const [address] = useAddressQuery();
  const { page, nextPage, previousPage, setPageNumber } = usePaginate();
  const goToPrevPage = () => {
    const topListElement = document.getElementById(TOP_LIST_ID);
    if (topListElement) {
      topListElement.scrollIntoView({ behavior: 'smooth' });
    }
    previousPage();
  };

  const goToNextPage = () => {
    const topListElement = document.getElementById(TOP_LIST_ID);
    if (topListElement) {
      topListElement.scrollIntoView({ behavior: 'smooth' });
    }
    nextPage();
  };

  useEffect(() => {
    if (address) {
      // scroll to the top-list on address change
      const topListElement = document.getElementById(TOP_LIST_ID);
      if (topListElement) {
        topListElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [address]);

  const count = useMemo(() => {
    return projectList.data?.count || 0;
  }, [projectList?.data?.count]);

  const labelProjectsFound = useMemo(() => {
    if (count === 0) {
      return 'Viewing 0 projects';
    }

    return `Viewing ${formatInteger(count)} ${count === 1 ? 'project' : 'projects'}`;
  }, [count]);

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / PAGE_SIZE) : 1;
  }, [count, PAGE_SIZE]);

  if (!count && !projectList.isFetching) {
    if (selectedPolygonBoundsOnMap) {
      return (
        <FlexColumn sx={containerStyle}>
          <Text.BodyBold>No projects found at this parcel location</Text.BodyBold>
        </FlexColumn>
      );
    }
  }

  return (
    <>
      <FlexRow>
        {projectList.isFetching ? (
          <Box sx={{ flex: 0 }}>
            <Spinner size="sm" mr="padding_8" />
          </Box>
        ) : (
          <FlexRow.Left>
            <Text sx={projectsFoundStyle}>{labelProjectsFound}</Text>
          </FlexRow.Left>
        )}
        <FlexRow.Right sx={resultText}>
          <ToggleButton />
        </FlexRow.Right>
      </FlexRow>
      <ProjectList
        projectList={projectList?.data?.projects || []}
        isPolygonSelected={!!selectedPolygonBoundsOnMap}
      />
      <FlexRow sx={paginationContainer}>
        <Pagination
          currentPage={page}
          isLoading={projectList.isFetching}
          setPage={(pageNumber) => setPageNumber(pageNumber)}
          totalPages={totalPages}
          onNext={goToNextPage}
          onPrev={goToPrevPage}
        />
      </FlexRow>
    </>
  );
};

export default RenderResults;
